import {
  ActionButton,
  CalculateTimeSeriesOutputType,
  CalculateTimeSeriesSchema,
  CalculateTimeSeriesType,
  DataObjectGroupType,
  Form,
  FormAlerts,
  GetApiErrorMessage,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  useAlertContext,
  useDataObjectGroupsWithDescriptionsQuery,
  useGroupDescriptionOptions,
  useMutationErrors,
  useSystemClassifierSelectOptions,
  useTimeSeriesCalculateOutputData,
  createApiErrorTranslator,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import React, { FC } from 'react';
import translations from './TimeSeriesActionModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TimeSeriesOutputsTable } from './TimeSeriesOutputsTable';
import { useWatch } from 'react-hook-form';
import { OutputQuickSelect } from './OutputQuickSelect';

const { addTranslations, t, exists } = i18nNsFactory(
  'time-series-action-modal',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = createApiErrorTranslator({
  t,
  exists,
});

export interface TimeSeriesCalculationModalProps {
  validate?: boolean;
  onClose: () => void;
}

export const TimeSeriesCalculationModal: React.FC<
  TimeSeriesCalculationModalProps
> = ({ validate = false, onClose }) => {
  const { mutateAsync } = useTimeSeriesCalculateOutputData();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  return (
    <Form
      schema={CalculateTimeSeriesSchema}
      defaultValues={{ validate }}
      onSubmit={(data: CalculateTimeSeriesType) => {
        resetAlert();
        clearMutationErrors();
        return mutateAsync({ data })
          .then(() => {
            onClose();
            setSuccess(t('alert.success.calculated'));
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <TimeSeriesCalculationModalInner validate={validate} onClose={onClose} />
    </Form>
  );
};

const TimeSeriesCalculationModalInner: FC<{
  calculate?: boolean;
  validate?: boolean;
  isLoading?: boolean;
  onClose: () => void;
}> = (props) => {
  const { validate, isLoading, onClose } = props;
  const { outputs } = useWatch<CalculateTimeSeriesType>();
  const { data: groupsWithDescriptions, isFetching } =
    useDataObjectGroupsWithDescriptionsQuery({
      groupType: DataObjectGroupType.O,
      active: true,
    });
  const groupDescriptionOptions = useGroupDescriptionOptions(
    groupsWithDescriptions,
    false,
  );
  const { SOURCE_DATA_VERSION: sourceDataVersionOptions } =
    useSystemClassifierSelectOptions(['SOURCE_DATA_VERSION']);

  return (
    <Modal
      onCancel={onClose}
      width="60rem"
      loading={isLoading}
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton type="abort" onClick={onClose} />
            <ActionButton
              type="formSubmit"
              disabled={!outputs || outputs.length === 0}
              label={
                validate
                  ? t('button.calculateAndValidate')
                  : t('button.calculate')
              }
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{t('title')}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <OutputQuickSelect
          options={groupDescriptionOptions}
          sourceDataVersionOptions={sourceDataVersionOptions}
          isLoading={isFetching}
        />
        <Padding top>
          <TimeSeriesOutputsTable<CalculateTimeSeriesOutputType>
            groupsWithDescriptionsOptions={groupDescriptionOptions}
            isLoading={isFetching}
            columnConfig={{
              sourceDataVersionId: {
                accessor: (rowData) => rowData.sourceDataVersionId,
                options: sourceDataVersionOptions,
              },
            }}
          />
        </Padding>
      </Padding>
    </Modal>
  );
};
